import SensorInventory from './SensorInventory';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import AdminOnly from 'components/AdminOnly';
import { useAnomalies } from 'Views/Monitoring/anomalies/useAnomalies';
import { SensorType } from 'general/enums';
import DeviceWorkspace from 'Views/Monitoring/UnifiedAlerts/DeviceWorkspace';

interface Props {
  orgId: string;
  sensorId: string;
  sensorType?: string;
}

function formatDate(date: Date | null) {
  return date?.toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen' }) || 'Ikke registreret';
}

function AnomalySummary({ orgId, sensorId }: Pick<Props, 'orgId' | 'sensorId'>) {
  const { query, events, scan } = useAnomalies({ orgId, sensorId });
  // Keep the display scoped even while a query is changing or refreshing.
  const scopedEvents = events.filter(
    (event) => event.orgId === orgId && event.sensorId === sensorId
  );
  const pending = scopedEvents.filter((event) => event.review.status === 'pending').length;
  const investigating = scopedEvents.filter(
    (event) => event.review.status === 'investigating'
  ).length;
  const scope = new URLSearchParams({ orgId, sensorId }).toString();
  const paused = scan?.control?.enabled === false;
  const scanStale =
    !scan?.lastCompletedAt ||
    Date.now() - scan.lastCompletedAt > 3 * 60 * 60 * 1000 ||
    (scan.lastFailedAt || 0) > scan.lastCompletedAt;

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex items-center justify-between gap-3">
        <h4 className="font-medium">Afvigelser til vurdering</h4>
        <button
          type="button"
          className="text-sky-700 underline"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Opdatér afvigelser
        </button>
      </div>
      {query.isPending ? <p role="status">Indlæser afvigelser…</p> : null}
      {query.isError ? (
        <div role="alert">
          <p>Kunne ikke hente afvigelser.</p>
          <button
            type="button"
            className="text-sky-700 underline"
            onClick={() => void query.refetch()}
          >
            Prøv igen
          </button>
        </div>
      ) : !query.isPending ? (
        <>
          <p>
            {pending} afventer vurdering · {investigating} undersøges
          </p>
          <p className="text-xs text-slate-500">
            Fælles analyse senest afsluttet:{' '}
            {formatDate(scan?.lastCompletedAt ? new Date(scan.lastCompletedAt) : null)}
          </p>
          {paused || scanStale ? (
            <p className="text-xs text-amber-800" role="status">
              {paused
                ? 'Den fælles afvigelsesanalyse er sat på pause.'
                : 'Afvigelsesanalysen er ikke opdateret som forventet.'}{' '}
              Historikken bekræfter ikke anlæggets tilstand lige nu.
            </p>
          ) : null}
          {query.hasNextPage ? (
            <p className="text-xs text-slate-500">
              Antal gælder kun indlæste afvigelser. Åbn oversigten for at hente flere.
            </p>
          ) : null}
        </>
      ) : null}
      <Link className="text-sky-700 underline" to={`/monitoring/anomalies?${scope}`}>
        Se sensorens afvigelser
      </Link>
    </div>
  );
}

function InternalOverviewContent({ orgId, sensorId, sensorType }: Props) {
  const supportsAnomalies = [
    SensorType.LEVEL_SENSOR_INTERNAL,
    SensorType.PLANT_MODULE,
    SensorType.GSM_PLANT,
  ].some((type) => type === sensorType);
  return (
    <div className="space-y-4 border-t px-4 py-4 text-sm">
      <DeviceWorkspace
        key={`${orgId}:${sensorId}`}
        orgId={orgId}
        sensorId={sensorId}
        statusContent={
          supportsAnomalies ? <AnomalySummary orgId={orgId} sensorId={sensorId} /> : null
        }
      />
    </div>
  );
}

function InternalOverviewSection(props: Props) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  return (
    <section className="my-4 rounded-lg border border-slate-200 bg-slate-50">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left text-sm font-medium text-slate-700"
        onClick={() => setOpen((current) => !current)}
      >
        <ShieldCheck aria-hidden="true" className="h-4 w-4" />
        AKS intern · Status og indstillinger
        <ChevronDown
          aria-hidden="true"
          className={`ml-auto h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div id={contentId}>{open ? <InternalOverviewContent {...props} /> : null}</div>
    </section>
  );
}

export default function SensorInternalOverview(props: Props) {
  if (!props.orgId || !props.sensorId) return null;
  // Mount fetching children only after both the permission gate and expansion.
  return (
    <>
      <AdminOnly>
        <InternalOverviewSection key={`${props.orgId}/${props.sensorId}`} {...props} />
      </AdminOnly>
      <SensorInventory {...props} />
    </>
  );
}
